import {
  Autocomplete,
  Box,
  FormHelperTextProps,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import { getAriaDescribedByValue } from "core/model/utils/strings";
import {
  CUSTOM_BLACK,
  KEYBOARD_FOCUS_OUTLINE,
  hexWithOpacity,
} from "ds_legacy/materials/colors";
import { FONT_SIZE_14 } from "ds_legacy/materials/typography";
import {
  UseCustomAlgoliaSearchClientProps,
  useAlgoliaSearchClient,
} from "dsl/hooks/useAlgoliaSearchClient";
import { debounce } from "lodash";
import React, { CSSProperties, ReactNode, useEffect, useState } from "react";
import {
  FormElementProps,
  FormElementRenderProps,
  isValid,
} from "react-forms-state";
import {
  AutocompleteExposed,
  AutocompleteProvided,
  BasicDoc,
  Hit,
} from "react-instantsearch-core";
import {
  Configure,
  InstantSearch,
  connectAutoComplete,
} from "react-instantsearch-dom";
import { useTranslations } from "translations";
import { getHelperText } from "../Validation";

type ConfigureProps = {
  filters?: string;
  hitsPerPage?: number;
};

export type ComboBoxProps<T extends object> = {
  ariaDescribedBy?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  enableAutoComplete?: boolean;
  enableAutoHighlight?: boolean;
  shouldClearOnBlur?: boolean;
  isDisabled?: boolean;
  elementName: string;
  hasError?: boolean;
  filterOptions?: (options: T[], state: { inputValue: string }) => T[];
  formControlSx?: CSSProperties;
  getOptionLabel: (option: T) => string;
  helperText?: ReactNode;
  inputSx?: CSSProperties;
  inputValue?: string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  label?: string;
  labelSx?: CSSProperties;
  isLoading?: boolean;
  noOptionsText?: string;
  onChange?: (event: React.SyntheticEvent, value: T | null) => void;
  onInputChange?: (event: React.SyntheticEvent, value: string) => void;
  options: T[];
  placeholder?: string;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
  ) => ReactNode;
  isRequired?: boolean;
  textFieldInputProps?: TextFieldProps["InputProps"];
  value?: T | null;
  variant?: TextFieldProps["variant"];
  wrapperSx?: CSSProperties;
};

export type ConnectedComboBoxProps<T extends object> = Omit<
  ComboBoxProps<T>,
  "onChange" | "error" | "helperText"
> &
  Pick<FormElementProps, "onChange">;

export type AlgoliaAutoCompleteBaseProps<
  TDoc extends BasicDoc,
  T extends object,
> = {
  getOptionLabel: (hit: Hit<TDoc>) => string;
  getValue: (hit: Hit<TDoc>) => T | null;
  minCharsForRefinement?: number;
  renderOption?: (hit: Hit<TDoc>) => ReactNode;
};

type AlgoliaAutoCompleteProps<
  TDoc extends BasicDoc,
  T extends object,
> = AlgoliaAutoCompleteBaseProps<TDoc, T> &
  AutocompleteExposed & {
    ariaDescribedBy?: string;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    elementName: string;
    errorMessage?: ReactNode;
    formControlSx?: CSSProperties;
    inputSx?: CSSProperties;
    isDisabled?: boolean;
    isInvalid?: boolean;
    isRequired?: boolean;
    label?: string;
    labelSx?: CSSProperties;
    noOptionsText?: string;
    onSelectionChange?: (value: T | null) => void;
    placeholder?: string;
    textFieldInputProps?: TextFieldProps["InputProps"];
    variant?: TextFieldProps["variant"];
    wrapperSx?: CSSProperties;
  };

type ConnectedAlgoliaAutoCompleteProps<
  TDoc extends BasicDoc,
  T extends object,
> = Pick<FormElementProps, "elementName"> &
  Omit<
    AlgoliaAutoCompleteProps<TDoc, T>,
    | "currentRefinement"
    | "hits"
    | "onChange"
    | "refine"
    | "onSelectionChange"
    | "isInvalid"
    | "errorMessage"
    | "formValue"
  > & {
    onLocalSelectionChange?: (value: T | null) => void;
  };

export type AlgoliaComboBoxProps<
  TDoc extends BasicDoc,
  T extends object,
  Connected extends boolean,
> = (Connected extends true
  ? Omit<
      AlgoliaAutoCompleteProps<TDoc, T>,
      | "onSelectionChange"
      | "isInvalid"
      | "errorMessage"
      | "currentRefinement"
      | "hits"
      | "onChange"
      | "refine"
    > & {
      onLocalSelectionChange?: (value: T | null) => void;
    }
  : Omit<
      AlgoliaAutoCompleteProps<TDoc, T>,
      "currentRefinement" | "hits" | "onChange" | "refine"
    > & {
      onSelectionChange: (value: T | null) => void;
    }) &
  AlgoliaAutoCompleteBaseProps<TDoc, T> &
  ConfigureProps &
  UseCustomAlgoliaSearchClientProps & {
    connected?: Connected;
    defaultInputValue?: string;
  };

export function ComboBox<T extends object>({
  ariaDescribedBy,
  ariaLabel,
  ariaLabelledBy,
  elementName,
  enableAutoComplete = true,
  enableAutoHighlight = true,
  filterOptions,
  formControlSx,
  getOptionLabel,
  hasError,
  helperText,
  inputSx,
  inputValue,
  isDisabled,
  isLoading,
  isOptionEqualToValue,
  isRequired,
  label,
  labelSx,
  noOptionsText,
  onChange,
  onInputChange,
  options,
  placeholder,
  renderOption,
  shouldClearOnBlur = false,
  textFieldInputProps,
  value,
  variant = "standard",
  wrapperSx,
}: ComboBoxProps<T>) {
  const translations = useTranslations();
  const { updateA11yMessage } = useA11yMessage();
  const errorTextId = `${elementName}_errorText`;
  const noOptionsId = `${elementName}_noOptions`;

  // Announce when there are no options available
  useEffect(() => {
    if (options.length === 0 && inputValue) {
      const message =
        noOptionsText || translations.bToC.providerList.infoNoResults;
      updateA11yMessage(message);
    }
  }, [options.length, inputValue, noOptionsText, updateA11yMessage]);

  // Create aria-describedby value including error text
  const ariaDescribedByValue = getAriaDescribedByValue([
    hasError && errorTextId,
  ]);

  return (
    <Box
      data-testid={`combobox-wrapper-${elementName}`}
      sx={{
        ...wrapperSx,
        display: "inline-block",
        position: "relative",
      }}
    >
      <Autocomplete
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        autoComplete={enableAutoComplete}
        autoHighlight={enableAutoHighlight}
        clearOnBlur={shouldClearOnBlur}
        componentsProps={{
          clearIndicator: {
            "aria-label": translations.actions.clear,
          },
          paper: {
            sx: (theme) => ({
              "@media (forced-colors: active)": {
                "& .MuiAutocomplete-listbox li:hover, & .MuiAutocomplete-listbox li.Mui-focused":
                  {
                    backgroundColor: "[Canvas] !important",
                    color: "[CanvasText] !important",
                    outline: "2px solid !important",
                    outlineColor: "[Highlight] !important",
                  },
              },

              "& .MuiAutocomplete-listbox li": {
                "&:hover, &.Mui-focused": {
                  backgroundColor: hexWithOpacity(
                    theme.palette.primary.main,
                    30,
                  ),
                  outline: "2px solid",
                  outlineColor: KEYBOARD_FOCUS_OUTLINE,
                },
                outlineOffset: "-2px",

                transition: "outline-color 0.2s ease",
              },
            }),
          },
          popupIndicator: {
            "aria-label": translations.actions.showResults,
          },
        }}
        disabled={isDisabled}
        filterOptions={filterOptions || ((x) => x)}
        getOptionLabel={getOptionLabel}
        inputValue={inputValue}
        isOptionEqualToValue={isOptionEqualToValue}
        loading={isLoading}
        noOptionsText={
          <span
            data-testid={`combobox-${elementName}-no-options`}
            id={noOptionsId}
          >
            {noOptionsText || translations.bToC.providerList.infoNoResults}
          </span>
        }
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={isDisabled}
            error={hasError}
            FormHelperTextProps={{
              id: errorTextId,
              ...({
                "data-testid": `combobox-helpertext-${elementName}`,
              } as FormHelperTextProps),
            }}
            helperText={helperText}
            InputLabelProps={{
              sx: {
                color: CUSTOM_BLACK,
                fontSize: FONT_SIZE_14,
                ...labelSx,
              },
            }}
            inputProps={{
              "aria-describedby": ariaDescribedByValue || ariaDescribedBy,
              "aria-invalid": hasError ? true : undefined,
              "data-testid": `combobox-${elementName}`,
              name: elementName,
              ...params.inputProps,
            }}
            InputProps={{
              ...params.InputProps,
              ...textFieldInputProps,
              sx: {
                "& .MuiInputBase-input::placeholder": {
                  opacity: 0.8, // To ensure color contrast requirements
                },
                fontSize: FONT_SIZE_14,
                ...inputSx,
              },
            }}
            label={label}
            placeholder={placeholder}
            required={isRequired}
            size="small"
            sx={{
              fontSize: FONT_SIZE_14,
              width: "100%",
              ...formControlSx,
            }}
            variant={variant}
          />
        )}
        renderOption={renderOption}
        value={value}
        onChange={onChange}
        onInputChange={onInputChange}
      />
    </Box>
  );
}

function ConnectedAlgoliaAutoComplete<TDoc extends BasicDoc, T extends object>(
  props: ConnectedAlgoliaAutoCompleteProps<TDoc, T> & {
    onLocalSelectionChange?: (value: T | null) => void;
  },
) {
  const translations = useTranslations();

  return (
    <FormElementRenderProps elementName={props.elementName}>
      {({ getValue: _, onChange, validation, value, ...rest }) => {
        return (
          <AlgoliaAutoComplete
            {...props}
            {...rest}
            defaultRefinement={value ? props.getOptionLabel(value) : undefined}
            elementName={props.elementName}
            errorMessage={getHelperText({
              hasCustomValidation: true,
              translations,
              validation,
            })}
            isInvalid={!isValid(validation)}
            onSelectionChange={(selectedValue) => {
              onChange(selectedValue);
              props.onLocalSelectionChange?.(selectedValue);
            }}
          />
        );
      }}
    </FormElementRenderProps>
  );
}

const AlgoliaAutoComplete = connectAutoComplete(
  ({
    ariaDescribedBy,
    ariaLabel,
    ariaLabelledBy,
    currentRefinement,
    elementName,
    errorMessage,
    formControlSx,
    getOptionLabel,
    getValue,
    hits,
    inputSx,
    isInvalid,
    isRequired,
    label,
    labelSx,
    minCharsForRefinement = 1,
    noOptionsText,
    onSelectionChange,
    placeholder,
    refine,
    renderOption,
    textFieldInputProps,
    variant = "standard",
    wrapperSx,
  }: AutocompleteProvided & AlgoliaAutoCompleteProps<any, any>) => {
    const [inputValue, setInputValue] = useState<string>(
      currentRefinement ?? "",
    );
    const [selectedValue, setSelectedValue] = useState<any>(null);
    const [isUserTyping, setIsUserTyping] = useState(false);

    const debouncedRefine = debounce((value: string) => {
      if (value.length >= minCharsForRefinement) {
        refine(value);
      }
    }, 200);

    const handleInputChange = (
      event: React.SyntheticEvent,
      newInputValue: string,
    ) => {
      setIsUserTyping(true);
      setInputValue(newInputValue);

      if (newInputValue === "") {
        // Clear search results when input is empty
        refine("");
        onSelectionChange?.("");
        setSelectedValue(null);
      } else {
        debouncedRefine(newInputValue);
      }
    };

    const handleChange = (event: React.SyntheticEvent, newValue: any) => {
      setIsUserTyping(false);
      setSelectedValue(newValue);
      onSelectionChange?.(newValue ? getValue(newValue) : null);

      if (newValue) {
        setInputValue(getOptionLabel(newValue));
      }
    };

    // Sync input value with currentRefinement when it changes externally
    useEffect(() => {
      if (!isUserTyping && currentRefinement !== inputValue) {
        setInputValue(currentRefinement ?? "");
      }
    }, [currentRefinement, isUserTyping]);

    return (
      <ComboBox
        ariaDescribedBy={ariaDescribedBy}
        ariaLabel={ariaLabel}
        ariaLabelledBy={ariaLabelledBy}
        elementName={elementName}
        formControlSx={formControlSx}
        getOptionLabel={getOptionLabel}
        hasError={isInvalid}
        helperText={errorMessage}
        inputSx={inputSx}
        inputValue={inputValue}
        isOptionEqualToValue={(option, value) =>
          option.objectID === value.objectID
        }
        isRequired={isRequired}
        label={label}
        labelSx={labelSx}
        noOptionsText={noOptionsText}
        options={hits}
        placeholder={placeholder}
        renderOption={
          renderOption
            ? (listItemProps, option) => {
                const { key: listItemKey, ...restListItemProps } =
                  listItemProps as React.HTMLAttributes<HTMLLIElement> & {
                    key?: React.Key;
                  };
                return (
                  <li key={listItemKey} {...restListItemProps}>
                    {renderOption(option)}
                  </li>
                );
              }
            : undefined
        }
        textFieldInputProps={textFieldInputProps}
        value={selectedValue}
        variant={variant}
        wrapperSx={wrapperSx}
        onChange={handleChange}
        onInputChange={handleInputChange}
      />
    );
  },
);

export function AlgoliaComboBox<
  TDoc extends BasicDoc,
  T extends object,
  Connected extends boolean = boolean,
>({
  algoliaAnalyticsName,
  connected,
  filters,
  hitsPerPage = 50,
  indexName,
  indexWithEnv,
  ...props
}: AlgoliaComboBoxProps<TDoc, T, Connected>) {
  const { analyticsTags, indexNameWithEnv, searchClient } =
    useAlgoliaSearchClient({ algoliaAnalyticsName, indexName, indexWithEnv });

  return (
    <InstantSearch indexName={indexNameWithEnv} searchClient={searchClient}>
      <Configure
        analyticsTags={analyticsTags}
        clickAnalytics
        filters={filters}
        hitsPerPage={hitsPerPage}
      />
      {connected ? (
        <ConnectedAlgoliaAutoComplete {...props} />
      ) : (
        <AlgoliaAutoComplete
          {...props}
          defaultRefinement={props.defaultInputValue}
        />
      )}
    </InstantSearch>
  );
}
